import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

const APPETITE_HELP = {
  small: 'A few days. One person or pair, done inside a couple of weeks.',
  big: 'A full cycle. The whole six weeks, and nothing else alongside it.',
};

const empty = {
  title: '',
  problem: '',
  appetite: 'big',
  solution: '',
  rabbitHoles: '',
  noGos: '',
  status: 'shaping',
};

/**
 * Writing down a pitch: the problem, the appetite, the shape, and the two things
 * that keep a cycle honest.
 *
 * Rabbit holes and no-gos get their own fields rather than being folded into the
 * solution, because they are the part teams skip and the part that decides whether
 * hammering later feels like a decision or a defeat.
 */
const PitchDialog = ({ open, pitch, onClose, onSubmit }) => {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm(pitch ? {
      title: pitch.title || '',
      problem: pitch.problem || '',
      appetite: pitch.appetite || 'big',
      solution: pitch.solution || '',
      rabbitHoles: pitch.rabbitHoles || '',
      noGos: pitch.noGos || '',
      status: pitch.status || 'shaping',
    } : empty);
    setError(null);
  }, [open, pitch]);

  const set = (field) => (event) => setForm(current => ({ ...current, [field]: event.target.value }));

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      setError('A pitch needs a title.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSubmit(form);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save the pitch.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{pitch ? 'Reshape this pitch' : 'Shape something'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Title"
            value={form.title}
            onChange={set('title')}
          />

          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Problem"
            placeholder="The raw need, in the words of whoever felt it"
            value={form.problem}
            onChange={set('problem')}
          />

          <TextField
            select
            fullWidth
            size="small"
            label="Appetite"
            value={form.appetite}
            onChange={set('appetite')}
            helperText={APPETITE_HELP[form.appetite]}
          >
            <MenuItem value="small">Small batch</MenuItem>
            <MenuItem value="big">Big batch</MenuItem>
          </TextField>

          <TextField
            fullWidth
            multiline
            minRows={3}
            size="small"
            label="Solution"
            placeholder="The elements and how they connect. Enough to build from, loose enough to leave room."
            value={form.solution}
            onChange={set('solution')}
          />

          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Rabbit holes"
            placeholder="Where this could swallow the cycle if nobody says so now"
            value={form.rabbitHoles}
            onChange={set('rabbitHoles')}
          />

          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="No-gos"
            placeholder="Explicitly not part of this, so cutting it later is not a fight"
            value={form.noGos}
            onChange={set('noGos')}
          />

          <TextField
            select
            fullWidth
            size="small"
            label="Status"
            value={form.status}
            onChange={set('status')}
            helperText={form.status === 'ready'
              ? 'On the table. If nobody bets on it for two months it goes stale and needs reshaping.'
              : 'Still being worked out. It will not show up as ready to bet on.'}
          >
            <MenuItem value="shaping">Still shaping</MenuItem>
            <MenuItem value="ready">Ready to bet on</MenuItem>
            <MenuItem value="archived">Archived</MenuItem>
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Saving…' : pitch ? 'Save' : 'Add the pitch'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PitchDialog;
